import firebase from "firebase/app";
import "firebase/auth";

import { hasOwnProperty, definedMap } from "collection-utils";

import { initFirebase } from "./firebase";
import { checkString, panic, nullToUndefined, isResponseOK } from "common/Support";
import { getAuth2, setGAPIAccessToken, googleDriveScope } from "./google-api";
import { logError, logInfo } from "common/debug-print";
import { saveUserUpdates, getReferralID } from "./firebase-functions";
import { getAppFacilities } from "webapp/lib/app-renderer";
import { UserDataUpdates, GetOAuth2TokensForGoogleSheetsBody } from "common/firebase-function-types";
import { trackEvent } from "../analytics";
import { callCloudFunctionWeb } from "sharedUI/lib/function-utils";
import queryString from "querystring";
import * as routes from "../routes";
import { showAcceptCancelModal, showFailureModal } from "designer/ui/smart/modals/modals";

export interface AuthenticatedUser {
    firebaseUser: firebase.User;
    googleAccessToken: string | undefined;
}

export function isAuthenticatedUser(x: unknown): x is AuthenticatedUser {
    return hasOwnProperty(x, "firebaseUser");
}

let authenticatedUser: AuthenticatedUser | undefined | "loading" = "loading";

let didInit = false;
// FIXME: We're calling this function everywhere "just in case".
// It sounds like a smell of using global _stateful_ things (like firebase)
function init(): void {
    if (didInit) return;

    initFirebase();
    firebase.auth().onAuthStateChanged(authStateChanged, e => logError("Firebase auth failed", e));

    didInit = true;
}

type AuthStateChangedCallback = (u: firebase.User | undefined) => void;

const callbacks: AuthStateChangedCallback[] = [];
let callbacksEnabled = true;

export function isFirebaseUserSignedIn(): boolean {
    return authenticatedUser !== undefined && authenticatedUser !== "loading";
}

export function onAuthStateChanged(callback: AuthStateChangedCallback): void {
    init();

    /**
     * We need to push the callback to the global array _before_ calling it
     * so the callback can remove itself from the array.
     *
     * Example:
     *
     * const callback = () => {
     *   removeOnAuthStateChanged(callback);
     *   // Do whatever...
     * }
     * onAuthStateChanged(callback);
     *
     * This callback should only run once.
     */
    callbacks.push(callback);

    // We trigger the provided auth callback immediately if we have a valid
    // auth state for one particular use case: we want to avoid popping login
    // screens if we can. In order to do this, we have to be _certain_ that
    // we're not logged in.
    //
    // The default state, before Firebase Auth completes, is "loading", but we
    // don't have any control over how long this process takes. It may be the
    // case, when we want to establish the auth callback, that we already are
    // certain of our auth state. So, if we are, we immediately fire the
    // provided callback.
    if (authenticatedUser !== "loading" && callbacksEnabled) {
        callback(definedMap(authenticatedUser, au => au.firebaseUser));
    }
}

export function removeOnAuthStateChanged(callback: AuthStateChangedCallback): void {
    const removeIndex = callbacks.indexOf(callback);
    if (removeIndex < 0) return;

    callbacks.splice(removeIndex, 1);
}

async function setAuthenticatedUser(user: AuthenticatedUser | undefined): Promise<void> {
    const stateChanged =
        authenticatedUser === "loading" ||
        (user === undefined) !== (authenticatedUser === undefined) ||
        (user !== undefined &&
            authenticatedUser !== undefined &&
            user.firebaseUser.uid !== authenticatedUser.firebaseUser.uid);
    authenticatedUser = user;

    if (user !== undefined && user.googleAccessToken !== undefined) {
        await setGAPIAccessToken(user.googleAccessToken);
    }

    if (!stateChanged) return;
    if (!callbacksEnabled) return;

    for (const callback of callbacks) {
        try {
            callback(definedMap(user, u => u.firebaseUser));
        } catch (e: unknown) {
            logError("Error calling auth state changed callback", e);
        }
    }
}

// Returns the access token, or `true` if the user hasn't yet granted
// offline access, or `false` if something went wrong.
interface GetAccessTokenSuccessfulResponse {
    error: false;
    accessToken: string;
}

// There are many possible reasons for this to fail, but we only check for either
// the 403 case, or any other (formerly 403 returned `true` and any other error `false`).
// Here, a forbidden response means the user revoked permissions for glide.
type GetAccessTokenFailureReason = "forbidden" | "generic";

interface GetAccessTokenFailedResponse {
    error: true;
    reason: GetAccessTokenFailureReason;
}

type GetAccessTokenResponse = GetAccessTokenSuccessfulResponse | GetAccessTokenFailedResponse;

function accessTokenError(reason: GetAccessTokenFailureReason): GetAccessTokenResponse {
    return {
        error: true,
        reason,
    };
}

function accessTokenSuccess(accessToken: string): GetAccessTokenResponse {
    return {
        error: false,
        accessToken,
    };
}

function isSuccessAccessTokenResponse(response: unknown): response is GetAccessTokenSuccessfulResponse {
    if (typeof response === "object" && response !== null) {
        const responseAsSuccess = response as GetAccessTokenSuccessfulResponse;

        return responseAsSuccess.error === false && typeof responseAsSuccess.accessToken === "string";
    }

    return false;
}

async function getAccessTokenForCurrentFirebaseUser(): Promise<GetAccessTokenResponse> {
    let auth2: gapi.auth2.GoogleAuth | undefined;
    try {
        // @ts-ignore
        auth2 = await getAuth2();
    } catch {
        return accessTokenError("generic");
    }
    logInfo("Got auth2");
    if (auth2 === undefined) return accessTokenError("generic");
    // You can be signed out of Google but still get valid access tokens.
    // The access tokens will be useless, because you aren't signed in.
    // We'll just bail if you aren't signed in because you wouldn't be
    // able to use the result anyway.
    if (!(auth2 as gapi.auth2.GoogleAuth).isSignedIn.get()) return accessTokenError("generic");

    const response = await getAppFacilities().callAuthCloudFunction("getOAuth2TokensForGoogleSheets", {});
    if (response === undefined) return accessTokenError("generic");
    if (response.status === 403) return accessTokenError("forbidden");
    if (!response.ok) {
        logError("No access token from the server");
        return accessTokenError("generic");
    }

    const body = await response.json();
    if (!hasOwnProperty(body, "accessToken")) {
        return panic("Response to getAccessToken not recognized");
    }

    const accessToken = checkString(body.accessToken);

    return accessTokenSuccess(accessToken);
}

let gotFirstAuthStateChanged = false;

async function authStateChanged(firebaseUser: firebase.User | null): Promise<void> {
    if (!callbacksEnabled) return;

    gotFirstAuthStateChanged = true;

    if (firebaseUser === null) {
        logInfo("Not logged in");
        return setAuthenticatedUser(undefined);
    }

    // FIXME: This is pure cruft.
    // We used to be able to rely on Google auth not getting incessantly signed out
    // but on Safari with Intelligent Tracking Prevention enabled, we're always
    // signed out of Google as soon as we open the page.
    // We need to not rely so much on the googleAccessToken being around
    // in this particular codepath.
    const maybeGoogleAccessToken = routes.isPlayer() ? undefined : await getAccessTokenForCurrentFirebaseUser();

    const googleAccessToken = isSuccessAccessTokenResponse(maybeGoogleAccessToken)
        ? maybeGoogleAccessToken.accessToken
        : undefined;

    return setAuthenticatedUser({ firebaseUser, googleAccessToken });
}

export async function tryGetAccessTokenAndAuthenticate(): Promise<AuthenticatedUser | undefined> {
    if (authenticatedUser === undefined || authenticatedUser === "loading") return undefined;

    const maybeGoogleAccessToken = await getAccessTokenForCurrentFirebaseUser();

    const googleAccessToken = isSuccessAccessTokenResponse(maybeGoogleAccessToken)
        ? maybeGoogleAccessToken.accessToken
        : undefined;

    const user = { ...authenticatedUser, googleAccessToken };
    const googleProvider = authenticatedUser.firebaseUser.providerData.reduce((acc, cur) => {
        if (acc?.providerId === "google.com") {
            return acc;
        } else if (cur?.providerId === "google.com") {
            return cur;
        }
        return null;
    });
    const hasGoogle = googleProvider !== null;
    if (hasGoogle && googleAccessToken === undefined) {
        return undefined;
    }
    await setAuthenticatedUser(user);
    return user;
}

type AuthenticationFailureKind =
    | "declined-sign-in"
    | "popup-blocked"
    | "no-google-libraries"
    | "cookies-blocked"
    | "link-expired"
    | "unexpected-error";

export interface AuthenticationFailure {
    kind: AuthenticationFailureKind;
    message?: string;
}

export interface GetGoogleUserAlreadySignedIn {
    error: false;
    alreadySignedIn: true;
    user: AuthenticatedUser;
}

export interface GetGoogleUserSuccessResponse {
    error: false;
    alreadySignedIn: false;
    user: gapi.auth2.GoogleUser;
}

export function isGoogleUserError(
    response: GetGoogleUserSuccessResponse | AuthenticationFailure | GetGoogleUserAlreadySignedIn
): response is AuthenticationFailure {
    return (response as AuthenticationFailure).kind !== undefined;
}

export function isGoogleUserAlreadySignedIn(
    response: GetGoogleUserSuccessResponse | AuthenticationFailure | GetGoogleUserAlreadySignedIn
): response is GetGoogleUserAlreadySignedIn {
    return (response as GetGoogleUserAlreadySignedIn).alreadySignedIn === true;
}

// DOES NOT THROW! Returns errors specified above.
export async function getGoogleUserForSignIn(): Promise<
    GetGoogleUserSuccessResponse | AuthenticationFailure | GetGoogleUserAlreadySignedIn
> {
    // Not sure if we have to `init` here. This global dependency is tricky.
    // Calling `init` everywhere looks like a smell.
    init();

    // Check if you're already logged in
    const maybeAuthenticatedUser = await tryGetAccessTokenAndAuthenticate();
    if (maybeAuthenticatedUser !== undefined) {
        return {
            error: false,
            alreadySignedIn: true,
            user: maybeAuthenticatedUser,
        };
    }

    callbacksEnabled = false;

    try {
        const auth2 = await getAuth2();
        if (auth2 === undefined) {
            return {
                kind: "unexpected-error",
                message: "Could not connect to Google.",
            };
        }

        const googleUser = await (auth2 as gapi.auth2.GoogleAuth).signIn({ prompt: "select_account" });
        if (!googleUser.isSignedIn()) {
            trackEvent("sign in failed", { reason: "Google user not signed in" });

            await setAuthenticatedUser(undefined);
            return {
                kind: "unexpected-error",
                message: "Sign up did not stick.",
            };
        }

        return {
            error: false,
            alreadySignedIn: false,
            user: googleUser,
        };
    } catch (err: unknown) {
        return handleSignInSignUpError(err);
    } finally {
        callbacksEnabled = true;
    }
}

export async function signInFromGoogleUser(
    googleUser: gapi.auth2.GoogleUser
): Promise<AuthenticatedUser | AuthenticationFailure> {
    // Not sure if we have to `init` here. This global dependency is tricky.
    // Calling `init` everywhere looks like a smell.
    init();

    const maybeAuthenticatedUser = await tryGetAccessTokenAndAuthenticate();
    if (maybeAuthenticatedUser !== undefined) return maybeAuthenticatedUser;

    callbacksEnabled = false;

    try {
        const authResponse = googleUser.getAuthResponse(true);
        const googleCredential = firebase.auth.GoogleAuthProvider.credential(
            authResponse.id_token,
            authResponse.access_token
        );
        const userCredential = await firebase.auth().signInWithCredential(googleCredential);

        if (userCredential.user === null) {
            trackEvent("sign in failed", { reason: "Firebase sign in failed" });

            await setAuthenticatedUser(undefined);
            throw new Error("Firebase sign-in resulted in a null user.");
        }

        const maybeGoogleAccessToken = await getAccessTokenForCurrentFirebaseUser();

        if (!isSuccessAccessTokenResponse(maybeGoogleAccessToken)) {
            // We still need to auto-signup if we reach this case.
            // A user could have revoked glide permissions from google,
            // so the email will be registered, but we'll get a `forbidden` response.
            if (maybeGoogleAccessToken.reason === "forbidden") {
                return await signUpWithGoogle(undefined, true);
            }

            trackEvent("sign in failed", { reason: "Could not get access token" });

            await firebase.auth().signOut();
            throw new Error("Could not getAccessTokenForCurrentFirebaseUser in signIn");
        }

        // Successfully signed in!
        trackEvent("sign in success", { provider: "google" });
        callbacksEnabled = true;

        const userWithToken = {
            firebaseUser: userCredential.user,
            googleAccessToken: maybeGoogleAccessToken.accessToken,
        };

        await setAuthenticatedUser(userWithToken);
        return userWithToken;
    } catch (err: unknown) {
        return handleSignInSignUpError(err);
    } finally {
        callbacksEnabled = true;
    }
}

export async function signInFromMagicLink(magicLink: string): Promise<AuthenticatedUser | AuthenticationFailure> {
    init();

    const maybeAuthenticatedUser = await tryGetAccessTokenAndAuthenticate();
    if (maybeAuthenticatedUser !== undefined) return maybeAuthenticatedUser;

    try {
        if (!gotFirstAuthStateChanged) {
            throw new Error("Trying to sign in before first Firebase Auth change in signInEmail");
        }

        callbacksEnabled = false;

        trackEvent("sign in start", { provider: "email" });

        const magicURL = new URL(magicLink);
        const queryStringParsed = queryString.parse(magicURL.search.substring(1));
        const { email } = queryStringParsed;
        const emailAddress = checkString(email);

        if (emailAddress === undefined) {
            throw new Error("URL doesn't include email address");
        }

        if (!firebase.auth().isSignInWithEmailLink(magicLink)) {
            throw new Error("Sign in link is not valid");
        }

        const userCredential = await firebase.auth().signInWithEmailLink(emailAddress, magicLink);

        if (userCredential.user === null) {
            trackEvent("sign in failed", { reason: "Firebase sign in failed" });

            callbacksEnabled = true;
            await setAuthenticatedUser(undefined);
            throw new Error("Firebase sign-in resulted in a null user.");
        }

        const firebaseUser = userCredential.user;
        if (firebaseUser === null) {
            trackEvent("sign up failed", { reason: "Firebase sign in failed" });
            logError("Could not sign in with Firebase");

            callbacksEnabled = true;
            throw new Error("Firebase sign-up resulted in a null user.");
        }

        const updates: UserDataUpdates = {
            email: nullToUndefined(firebaseUser.email),
            displayName: nullToUndefined(firebaseUser.displayName ?? firebaseUser.email),
            phoneNumber: nullToUndefined(firebaseUser.phoneNumber),
            photoURL: nullToUndefined(firebaseUser.photoURL),
            integrations: [],
        };

        const user = { firebaseUser, googleAccessToken: undefined };

        if (await saveUserUpdates(updates, {})) {
            trackEvent("sign up success", { user_id: firebaseUser.uid, referrer: getReferralID(), provider: "email" });
        }
        logInfo("Authenticated user", user);

        trackEvent("sign in success", { provider: "email" });
        callbacksEnabled = true;
        await setAuthenticatedUser(user);
        return user;
    } catch (err: unknown) {
        return handleSignInSignUpError(err);
    }
}

export async function setAuthPersistence(persistent: boolean) {
    init();

    await firebase
        .auth()
        .setPersistence(persistent ? firebase.auth.Auth.Persistence.LOCAL : firebase.auth.Auth.Persistence.SESSION);
}

export async function signUpWithGoogle(
    allowEmail: boolean | undefined,
    viaSignIn: boolean = false
): Promise<AuthenticatedUser | AuthenticationFailure> {
    init();

    const maybeAuthenticatedUser = await tryGetAccessTokenAndAuthenticate();
    if (maybeAuthenticatedUser !== undefined) return maybeAuthenticatedUser;

    // For brand new users, we're not sure if they're going to consent to
    // the tracking cookies, so we _have_ to shut off Firebase Auth persistence.
    // If we want it back then we'll set it appropriately in the auth state
    // callback of loadUser.
    await setAuthPersistence(false);

    // Note that handleSignInUpError() will return the appropriate "unexpected-error" string.
    // We are throwing these exceptions primarily to pick them up in Sentry.
    try {
        if (!gotFirstAuthStateChanged) {
            throw new Error("Trying to sign in before first Firebase Auth change in signUp");
        }
        callbacksEnabled = false;

        const auth2 = await getAuth2();
        if (auth2 === undefined) return { kind: "no-google-libraries" };

        trackEvent("sign up started", { provider: "google" });

        // This will throw if the user cancels.
        const { code } = await (auth2 as gapi.auth2.GoogleAuth).grantOfflineAccess({
            scope: googleDriveScope,
            // The types are busted. You can absolutely combine "consent"
            // and "select_account", as per the OAuth standard.
            prompt: viaSignIn ? "consent" : ("consent select_account" as any),
        });

        const body: GetOAuth2TokensForGoogleSheetsBody = { code };
        const response = await getAppFacilities().callAuthIfAvailableCloudFunction(
            "getOAuth2TokensForGoogleSheets",
            body,
            {}
        );
        if (!isResponseOK(response)) {
            trackEvent("sign up failed", { reason: "Could not get OAuth2 tokens" });
            logError("Could not get tokens for code", response);

            callbacksEnabled = true;
            throw new Error(`Could not getOAuth2TokensForGoogleSheets: ${definedMap(response, r => r.statusText)}`);
        }

        const { idToken, accessToken, refreshToken } = await response.json();
        logInfo("OAuth2 tokens", idToken, accessToken);

        const credential = firebase.auth.GoogleAuthProvider.credential(idToken, accessToken);
        const firebaseCredential = await firebase.auth().signInWithCredential(credential);

        const firebaseUser = firebaseCredential.user;
        if (firebaseUser === null) {
            trackEvent("sign up failed", { reason: "Firebase sign in failed" });
            logError("Could not sign in with Firebase");

            callbacksEnabled = true;
            throw new Error("Firebase sign-up resulted in a null user.");
        }

        const updates: UserDataUpdates = {
            email: nullToUndefined(firebaseUser.email),
            displayName: nullToUndefined(firebaseUser.displayName),
            phoneNumber: nullToUndefined(firebaseUser.phoneNumber),
            photoURL: nullToUndefined(firebaseUser.photoURL),
            flags: { allowEmail },
            integrations: [
                {
                    name: "google-drive",
                    kind: "google-drive",
                    authentication: {
                        kind: "oauth2-offline",
                        idToken,
                        refreshToken,
                    },
                },
            ],
        };

        const user = { firebaseUser, googleAccessToken: accessToken };

        if (await saveUserUpdates(updates, {})) {
            trackEvent("sign up success", { user_id: firebaseUser.uid, referrer: getReferralID(), provider: "google" });
        }
        logInfo("Authenticated user", user);

        callbacksEnabled = true;
        await setAuthenticatedUser(user);
        return user;
    } catch (err: unknown) {
        return handleSignInSignUpError(err);
    }
}

/**
 * If you're already signed in, this returns the current authenticated user.
 * If something goes wrong, this returns an AuthenticationFailure.
 * Else, if the magic link email was sent successfully, we return `true`.
 */
export async function signUpEmail(
    emailAddress: string,
    mode: "signUp" | "signIn"
): Promise<AuthenticatedUser | AuthenticationFailure | true> {
    init();

    const maybeAuthenticatedUser = await tryGetAccessTokenAndAuthenticate();
    if (maybeAuthenticatedUser !== undefined) return maybeAuthenticatedUser;

    // For brand new users, we're not sure if they're going to consent to
    // the tracking cookies, so we _have_ to shut off Firebase Auth persistence.
    // If we want it back then we'll set it appropriately in the auth state
    // callback of loadUser.
    await setAuthPersistence(false);

    // Note that handleSignInUpError() will return the appropriate "unexpected-error" string.
    // We are throwing these exceptions primarily to pick them up in Sentry.
    try {
        if (!gotFirstAuthStateChanged) {
            throw new Error("Trying to sign in before first Firebase Auth change in signUp");
        }
        callbacksEnabled = false;

        trackEvent(mode === "signIn" ? "sign in start" : "sign up started", { provider: "email" });

        const result = await createAndSendAuthLink(emailAddress, undefined, window.location.href);
        if (result === undefined) {
            throw new Error("Failed to create and send auth link.");
        } else if (result.status !== 200) {
            const message = await result.text();
            throw new Error(`Failed to create and send auth link: ${message}`);
        }

        trackEvent(mode === "signIn" ? "sign in link sent" : "sign up link sent", {});

        callbacksEnabled = true;
        return true;
    } catch (err: unknown) {
        return handleSignInSignUpError(err);
    }
}

export async function isEmailSignedUp(email: string): Promise<boolean> {
    const response = await callCloudFunctionWeb("isEmailSignedUp", {
        email,
    });

    if (response === undefined) {
        throw new Error("Failed to check if the account exists");
    }

    const { isSignedUp } = await response.json();

    return isSignedUp;
}

async function createAndSendAuthLink(
    emailAddress: string,
    displayName: string | undefined = undefined,
    targetURL: string | undefined = undefined
) {
    return callCloudFunctionWeb("createAndSendAuthLink", {
        emailAddress,
        displayName,
        targetURL,
    });
}

export function isThirdPartyCookieError(err: unknown) {
    return (
        hasOwnProperty(err, "error") &&
        hasOwnProperty(err, "details") &&
        err.error === "idpiframe_initialization_failed" &&
        err.details === "Cookies are not enabled in current environment."
    );
}

function handleSignInSignUpError(err: unknown): AuthenticationFailure {
    callbacksEnabled = true;
    let closeByUser = false;
    let popupBlocked = false;
    let expired = false;
    if (hasOwnProperty(err, "code")) {
        if (err.code === "auth/popup-closed-by-user") {
            closeByUser = true;
        } else if (err.code === "auth/popup-blocked-by-browser") {
            popupBlocked = true;
        } else if (err.code === "auth/invalid-action-code") {
            expired = true;
        }
    }
    if (hasOwnProperty(err, "error")) {
        if (err.error === "popup_closed_by_user") {
            closeByUser = true;
        } else if (err.error === "popup_blocked_by_browser") {
            popupBlocked = true;
        }
    }
    const cookiesBlocked = isThirdPartyCookieError(err);
    if (closeByUser) {
        trackEvent("sign in closed by user", {});
        return { kind: "declined-sign-in" };
    } else if (popupBlocked) {
        trackEvent("sign in blocked by popup blocker", {});
        return { kind: "popup-blocked" };
    } else if (cookiesBlocked) {
        // There's no point in sending analytics for this: we're in
        // a "high security" environment and they're likely already blocked.
        return { kind: "cookies-blocked" };
    } else if (expired) {
        return { kind: "link-expired" };
    } else {
        trackEvent("sign in failed", { reason: "Exception thrown" });
    }
    logError("error signing in", err);
    // Well, we don't actually know what the error is.
    // If it's a proper Error, we can get a message out of it.
    // But Google's libraries are nasty and will throw non-Errors.
    // So we need to fall back to this weird thing.
    //
    // You couldn't sign in, so you're already having a bad day.
    // At least this JSON will give us an idea of what went wrong.
    const message = err instanceof Error ? err.message : JSON.stringify(err, undefined, 4);
    return { kind: "unexpected-error", message };
}

export async function signOut(): Promise<void> {
    logInfo("signing out");

    // @ts-ignore
    const auth2: gapi.auth2.GoogleAuth = await getAuth2();
    if (auth2 !== undefined && auth2.isSignedIn.get()) {
        auth2.signOut();
    }

    await setAuthPersistence(false);
    await firebase.auth().signOut();
}

export enum GoogleAccessTokenFailureKind {
    UserCanceled = "canceled",
    SigInFailure = "sign-in-failure", // comes with an `AuthenticationFailure`
    NoAccess = "no-access",
}

export async function ensureGoogleAccessTokenIsPresent(): Promise<
    | { success: false; kind: GoogleAccessTokenFailureKind; failure?: AuthenticationFailure }
    | { success: true; token: string }
> {
    const authedUser = await tryGetAccessTokenAndAuthenticate();
    if (authedUser === undefined) {
        const cont = await showAcceptCancelModal(
            "Connect to Google",
            "To use Google Sheets, you must sign into Glide with a Google account. We recommend using a Google account with the same email as your Glide account.",
            "Sign in with Google",
            "Cancel"
        );
        if (!cont) return { success: false, kind: GoogleAccessTokenFailureKind.UserCanceled };
    }

    const maybeGoogleUser = await getGoogleUserForSignIn();

    if (isGoogleUserError(maybeGoogleUser)) {
        if (
            maybeGoogleUser.kind !== "cookies-blocked" &&
            maybeGoogleUser.kind !== "no-google-libraries" &&
            maybeGoogleUser.kind !== "declined-sign-in"
        ) {
            // FIXME: Why are we signing users out when they can't sign in with Google?
            await signOut();
        }
        return { success: false, kind: GoogleAccessTokenFailureKind.SigInFailure, failure: maybeGoogleUser };
    }

    if (isGoogleUserAlreadySignedIn(maybeGoogleUser)) {
        if (maybeGoogleUser.user.googleAccessToken === undefined) {
            await showFailureModal("This account doesn't have access to Google Sheets.");
            return { success: false, kind: GoogleAccessTokenFailureKind.NoAccess };
        }

        return { success: true, token: maybeGoogleUser.user.googleAccessToken };
    }

    const au = await signInFromGoogleUser(maybeGoogleUser.user);
    if (!isAuthenticatedUser(au)) {
        if (au.kind !== "cookies-blocked" && au.kind !== "no-google-libraries") {
            await signOut();
        }
        return { success: false, kind: GoogleAccessTokenFailureKind.SigInFailure, failure: au };
    }
    if (au.googleAccessToken === undefined) {
        await showFailureModal("This account doesn't have access to Google Sheets.");
        return { success: false, kind: GoogleAccessTokenFailureKind.NoAccess };
    }

    return { success: true, token: au.googleAccessToken };
}
